import React, { createContext, useContext, useRef, useState, useCallback } from 'react';

/**
 * In-tab, in-memory draft store. Keeps a user's in-progress edits alive across
 * route changes / component unmounts WITHIN a session. Drafts intentionally die
 * on reload or tab close — there is no sessionStorage/localStorage backing (a
 * later tier would add that, with different staleness risks).
 *
 * Keyed "entityType:id" (e.g. "customer:44", "invoice:812") so many records hold
 * drafts concurrently. Each entry captures a `baseline` — the record's server
 * version marker (updated_at) at the moment the draft was FIRST created — so the
 * restore flow can tell "safe to restore" from "the record changed while you were
 * away." `touchedSensitive` names the money/tax/quantity/shipping-document fields
 * the user changed, so the restore banner can flag them.
 *
 * The store never triggers app-wide re-renders: entries live in a ref, and each
 * field's live value is mirrored by the consuming screen's own local state
 * (useDraftState). The store is a persistence sink + a source-on-mount.
 */

export interface DraftEntry {
  fields: Record<string, any>;
  baseline: string | null;
  touchedSensitive: string[];
  createdAt: number;
}

interface DraftContextType {
  getDraft: (key: string) => DraftEntry | undefined;
  hasDraft: (key: string) => boolean;
  setDraftField: (key: string, field: string, value: any, opts?: { sensitive?: boolean }) => void;
  setBaseline: (key: string, value: string | null) => void;
  clearDraft: (key: string) => void;
}

// Deep-clone JSON-safe draft values so a screen mutating an array/object (e.g. the
// BOL pallet build) can never alias the stored draft, and a restored draft can
// never alias what a previous screen still holds. Scalars pass through.
export function cloneValue<T>(v: T): T {
  if (v === null || typeof v !== 'object') return v;
  try {
    return JSON.parse(JSON.stringify(v));
  } catch {
    return v;
  }
}

const DraftContext = createContext<DraftContextType | undefined>(undefined);

export const DraftProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const store = useRef<Map<string, DraftEntry>>(new Map());

  // Return a clone so a caller can never mutate the stored draft through the
  // returned object (writes are cloned too — aliasing is impossible in either
  // direction).
  const getDraft = useCallback((key: string): DraftEntry | undefined => {
    const e = store.current.get(key);
    if (!e) return undefined;
    return { ...e, fields: cloneValue(e.fields), touchedSensitive: [...e.touchedSensitive] };
  }, []);

  // A draft "exists" only once it has at least one field. A baseline-only entry
  // (primed after fetch, before any edit) is NOT a draft.
  const hasDraft = useCallback((key: string) => {
    const e = store.current.get(key);
    return !!e && Object.keys(e.fields).length > 0;
  }, []);

  const setDraftField = useCallback((key: string, field: string, value: any, opts?: { sensitive?: boolean }) => {
    let e = store.current.get(key);
    if (!e) {
      e = { fields: {}, baseline: null, touchedSensitive: [], createdAt: Date.now() };
      store.current.set(key, e);
    }
    e.fields[field] = cloneValue(value);
    if (opts?.sensitive && !e.touchedSensitive.includes(field)) {
      e.touchedSensitive.push(field);
    }
  }, []);

  // Prime/refresh the baseline. Only takes effect while there is no draft yet, so
  // the version the user STARTED editing from is preserved once a draft exists
  // (the restore flow compares that original baseline to the current server
  // version). Creating a baseline-only entry keeps hasDraft() false.
  const setBaseline = useCallback((key: string, value: string | null) => {
    let e = store.current.get(key);
    if (!e) {
      e = { fields: {}, baseline: value, touchedSensitive: [], createdAt: Date.now() };
      store.current.set(key, e);
      return;
    }
    if (Object.keys(e.fields).length === 0) {
      e.baseline = value;
    }
  }, []);

  const clearDraft = useCallback((key: string) => {
    store.current.delete(key);
  }, []);

  const value: DraftContextType = { getDraft, hasDraft, setDraftField, setBaseline, clearDraft };
  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
};

export const useDraft = (): DraftContextType => {
  const ctx = useContext(DraftContext);
  if (!ctx) {
    // Safe no-op fallback for components/tests rendered outside the provider —
    // they behave as if drafts are disabled.
    return {
      getDraft: () => undefined,
      hasDraft: () => false,
      setDraftField: () => {},
      setBaseline: () => {},
      clearDraft: () => {},
    };
  }
  return ctx;
};

/**
 * useState-compatible drop-in that mirrors its value into the draft store.
 * Initializes from the stored draft when one exists for key+field (restore),
 * else from `initial`. Every write updates BOTH local state and the store.
 * Pass { sensitive: true } for money/tax/quantity/shipping-document fields.
 */
export function useDraftState<T>(
  key: string,
  field: string,
  initial: T,
  opts?: { sensitive?: boolean },
): [T, (v: T | ((prev: T) => T)) => void] {
  const { getDraft, setDraftField } = useDraft();
  const sensitive = !!opts?.sensitive;

  const [value, setValue] = useState<T>(() => {
    const d = getDraft(key);
    if (d && Object.prototype.hasOwnProperty.call(d.fields, field)) {
      return cloneValue(d.fields[field]) as T;
    }
    return initial;
  });

  const set = useCallback((v: T | ((prev: T) => T)) => {
    setValue(prev => {
      const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v;
      setDraftField(key, field, next, { sensitive });
      return next;
    });
  }, [key, field, sensitive, setDraftField]);

  return [value, set];
}

export type DraftRestoreMode = 'none' | 'restored' | 'conflict';

export interface DraftRestoreController {
  mode: DraftRestoreMode;
  createdAt: number | null;
  changedFields: string[];       // field names the draft would change
  sensitiveLabels: string[];     // human labels of touched sensitive fields
  dismiss: () => void;           // hide the "restored" banner
  keepMine: () => void;          // conflict: accept the user's draft, acknowledge the new baseline
  discard: () => void;           // clear the draft (screen re-applies server values via onUseServer)
}

/**
 * Restore controller for a screen. Capture `hadDraftAtMount` BEFORE the user can
 * create a draft by typing; feed the fetched record's version marker once it's
 * known. Returns the mode the screen renders:
 *   - 'none'      → no prior draft; render nothing.
 *   - 'restored'  → draft matches the current server version; show a dismissible
 *                   banner (name sensitive fields via `labels`).
 *   - 'conflict'  → the record changed server-side since the draft; show a
 *                   blocking choice (never auto-apply, never silently discard).
 *
 * `onUseServer` is the screen's callback to re-populate fields from the freshly
 * fetched record when the user discards their draft.
 */
export function useDraftRestore(
  key: string,
  serverVersion: string | null | undefined,
  opts?: { labels?: Record<string, string>; onUseServer?: () => void },
): DraftRestoreController {
  const { getDraft, hasDraft, clearDraft, setBaseline } = useDraft();
  // Snapshot draft-existence once, before any keystroke can create one.
  const hadDraftAtMount = useRef<boolean | null>(null);
  if (hadDraftAtMount.current === null) hadDraftAtMount.current = hasDraft(key);

  const [dismissed, setDismissed] = useState(false);
  const [resolved, setResolved] = useState(false);

  const draft = getDraft(key);
  let mode: DraftRestoreMode = 'none';
  if (hadDraftAtMount.current && draft && !dismissed && !resolved) {
    // A null baseline can't be compared → treat as a benign restore.
    if (draft.baseline == null || serverVersion == null || draft.baseline === String(serverVersion)) {
      mode = 'restored';
    } else {
      mode = 'conflict';
    }
  }

  const labels = opts?.labels || {};
  const sensitiveLabels = draft ? draft.touchedSensitive.map(f => labels[f] || f) : [];
  const changedFields = draft ? Object.keys(draft.fields) : [];

  const dismiss = useCallback(() => setDismissed(true), []);

  const keepMine = useCallback(() => {
    // Acknowledge the newer server version so the same conflict doesn't re-fire.
    if (serverVersion != null) setBaseline(key, String(serverVersion));
    setResolved(true);
  }, [key, serverVersion, setBaseline]);

  const discard = useCallback(() => {
    clearDraft(key);
    setResolved(true);
    opts?.onUseServer?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, clearDraft]);

  return {
    mode,
    createdAt: draft ? draft.createdAt : null,
    changedFields,
    sensitiveLabels,
    dismiss,
    keepMine,
    discard,
  };
}
